import React, { useRef, useState } from 'react';
import AddNewPopUp, { Field, SaveUnresolvedError } from 'reusable/AddEditDelete/AddNew';
import Export from 'reusable/export';
import { CsvCondition, csvDateTime } from 'config/csv';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import ErrorPopup from 'reusable/ErrorPopup';
import { deviceAssignmentMessages } from 'config/apiErrors';
import { useClassifiedErrorMessages } from 'reusable/useSaveError';
import { useTranslation } from 'react-i18next';
import { assignableDevicePage } from './deviceCandidates';
import {
  CleanupPendingError,
  newRequestId,
  OutcomeUnknownError,
  registerDriver,
  tellsOriginalOutcome,
} from './registerDriver';
import {
  canonicalBody,
  establishesOutcome,
  REQUEST_ID_REUSE_MS,
  UnconfirmedCreate,
  unconfirmedStore,
} from 'config/idempotentCreate';
import { getSessionIdentity } from 'config/session';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onPageChange: (value: number) => void;
  driverData: any[];
  /** EV-142。表示している行の取得に成功した時点の条件。 */
  exportConditions?: CsvCondition[];
  /** ADM-14。途中で失敗したとき、一覧をサーバーの確定状態で取り直す。 */
  onChanged?: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  sortValue,
  onSearchChange,
  onSortChange,
  onPageChange,
  driverData,
  exportConditions,
  onChanged,
}) => {
  const { t } = useTranslation('driver');
  // ADM-15-AC1。通信断・403・5xx を区別した説明（以前は apiErrorMessages で区別しなかった）。
  const classifiedMessages = useClassifiedErrorMessages();
  const [errorPopupVisible, setErrorPopupVisible] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [popupTitle, setPopupTitle] = useState('');
  // EV-493。機器の候補を読み直させる印（登録の後。割り当てた機器は候補から外れる）。
  // 候補を読めなかったときの表示（EV-318）は、候補の欄（SearchCombobox）が出す。
  const [candidateVersion, setCandidateVersion] = useState(0);
  const fetchDeviceData = () => setCandidateVersion((version) => version + 1);

  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    // I12-2。ドライバーID は携帯番号（ID）の順（以前は自動で振る番号の順）。
    { label: t('sort.driver-id', { ns: 'common' }), value: 'phone' },
  ];

  // Field key mapping to match formData fields with the expected API keys
  const fieldKeyMap: { [key: string]: string } = {
    [t('add-driver.driver-name')]: 'DRIVER NAME',
    [t('add-driver.email')]: 'EMAIL ADDRESS',
    [t('add-driver.dob')]: 'DATE OF BIRTH',
    [t('add-driver.assigned-device')]: 'ASSIGNED DEVICE',
    [t('add-driver.contact')]: 'CONTACT NUMBER',
  };

  // EV-461。前回の登録の値を残した（同じ X-Request-ID で送り直すべき）失敗だったか。
  const keptDriverRecord = useRef(false);

  /**
   * EV-461（#387 の RV-703 と同じ型）。登録を1回の操作として送る。結果が分からなかった登録の X-Request-ID と本文は
   * タブに残し（config/idempotentCreate の unconfirmedStore('driver')）、同じ本文で押し直したら同じ値で送る
   * （前回が確定していればサーバーは前回の応答を返し、二重に作らない）。以前は押すたびに新しい値で送っていた。
   * 期限（REQUEST_ID_REUSE_MS）を過ぎた値は使わず新しい値にする（同じメールの運転者はサーバーが断るので二重にならない）。
   */
  const registerDriverOnce = async (payload: { [key: string]: any }) => {
    const store = unconfirmedStore('driver');
    const body = canonicalBody(payload);
    const stored = store.load();
    const reuse =
      stored && stored.body === body && Date.now() - stored.savedAt < REQUEST_ID_REUSE_MS ? stored : null;
    const requestId = reuse ? reuse.requestId : newRequestId();
    const record: UnconfirmedCreate = {
      requestId,
      body,
      savedAt: reuse ? reuse.savedAt : Date.now(),
      expiredWarned: false,
      session: getSessionIdentity(),
    };
    keptDriverRecord.current = false;
    store.save(record);
    try {
      const outcome = await registerDriver(payload, requestId);
      store.clear(requestId, body);
      return outcome;
    } catch (error) {
      if (error instanceof OutcomeUnknownError) {
        store.save(record, { onlyOwn: true });
      } else if (
        reuse &&
        !(error instanceof CleanupPendingError) &&
        !establishesOutcome(error) &&
        !tellsOriginalOutcome(error)
      ) {
        // RV-128 と同じ。前回の値で送り直して X-Request-ID の処理より前に断られた（401・403 など）。前回の結果は
        // まだ分からないので値を残す。
        store.save(record, { onlyOwn: true });
        keptDriverRecord.current = true;
      } else {
        // 結果が分かった失敗（項目のエラー・招待を送れず取り消した・後片付け待ち）か、新しい値の失敗。
        store.clear(requestId, body);
      }
      throw error;
    }
  };

  const AddNewDriverAPI = async (formData: { [key: string]: any }) => {
    // Transform formData to use keys expected by the API
    const transformedData = Object.keys(formData).reduce((acc, label) => {
      const key = fieldKeyMap[label] || label; // Use mapped key or default to label
      acc[key] = formData[label];
      return acc;
    }, {} as { [key: string]: any });

    try {
      const payload: any = {
        name: transformedData['DRIVER NAME'],
        display_name: transformedData['DRIVER NAME'],
        birthday: transformedData['DATE OF BIRTH'],
        phone: transformedData['CONTACT NUMBER'],
        email: transformedData['EMAIL ADDRESS'],
        devices: transformedData['ASSIGNED DEVICE']?.length > 0
          ? transformedData['ASSIGNED DEVICE'].map((device: any) => device.id)
          : [],
      };
      // EV-461。機器は選んだ順番に意味が無い（サーバーは集合として割り当てる）。ID の順にそろえ、同じ機器なら
      // 同じ本文（同じ X-Request-ID・サーバーの本文の照合も一致）にする（配送先の運転者と同じ。EV-139）。
      payload.devices = [...payload.devices].sort((a: any, b: any) => String(a).localeCompare(String(b)));

      // EV-023。機器の割当は devices に含め、サーバーが運転者の登録と1つの更新として
      // 確定する（割当の規則・台数の上限もそこで確かめる）。以前は登録の後に機器 API で
      // 同じ割当を1台ずつもう一度書き込み、その書き込みが失敗すると、実際には割り当て
      // 済みなのに「割当失敗」と表示していた。
      // EV-034。登録の操作ごとに X-Request-ID を付け、結果が分からない失敗では同じ値で
      // 送り直し、それでも分からなければ一覧をメールで照合する（registerDriver）。
      const outcome = await registerDriverOnce(payload);
      // PR #99 レビュー。画面全体を再読み込みすると、保存を待つ間に開き直した追加画面の
      // 入力まで消える。一覧と未割当の機器だけをサーバーの確定状態で取り直す。
      onChanged?.();
      fetchDeviceData();
      if (outcome === 'created-without-invitation') {
        // EV-085。登録は残ったが、招待メールは届いていない。運転者への案内を促す。
        setPopupTitle(t('add-driver.notice'));
        setErrorMessages([t('add-driver.invitation-not-sent')]);
        setErrorPopupVisible(true);
      } else if (outcome === 'role-added') {
        // PM 11/1。同じ会社の管理者だったので、そのアカウントに運転者の役割を足した（新しい招待ではない）。
        setPopupTitle(t('add-driver.notice'));
        setErrorMessages([t('add-driver.role-added')]);
        setErrorPopupVisible(true);
      }
    } catch (error: any) {
      setPopupTitle(t('ui.error', { ns: 'common' }));
      if (error instanceof CleanupPendingError) {
        // EV-079。招待は届かず、取り消しが済んでいない登録が残っている。一覧はサーバーの
        // 今の状態で取り直し（残った登録が見える）、その理由と次の手順を示す。入力は残す。
        setErrorMessages([t('add-driver.cleanup-pending')]);
        onChanged?.();
      } else if (error instanceof OutcomeUnknownError) {
        // EV-034。登録できたかを確かめられなかった。入力は残し、一覧は取り直す
        // （後から登録が確定していれば一覧に出る）。EV-461。入力は送った値のまま固定し、同じ値で送り直させる。
        setErrorMessages([t('add-driver.outcome-unknown')]);
        onChanged?.();
      } else {
        // 応答の値が文字列のとき join で落ちていた。code も表示していた。
        // BE-02。機器を割り当てられない理由（最上位の code）は画面の言語で出す。
        setErrorMessages(
          deviceAssignmentMessages(error, {
            inUse: t('ui.device-in-use'),
            notAvailable: t('ui.device-not-available'),
          }) ?? classifiedMessages(error)
        );
      }
      setErrorPopupVisible(true);
      console.error('Failed to post driver data', error);
      // ADM-15。運転者を作れなかったことを追加画面へ返し、閉じずに入力を残させる。
      // EV-461。結果が分からないまま（値を残した）なら、追加画面は入力を送った値のまま固定する。
      if (error instanceof OutcomeUnknownError || keptDriverRecord.current) throw new SaveUnresolvedError(error);
      throw error;
    }
  };

  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1); 
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    onPageChange(1); 
  };

  // I12-2。ドライバーID は携帯番号（ID）。自動で振る番号（#23 など）は出さない。
  const exportFields = [
    { header: 'Driver ID', field: 'phone' },
    { header: 'Driver Name', field: 'name' },
    // 2026-11-03 UAT A23。画面の列名（酒気帯び検知回数（累計））にそろえた。
    { header: 'Alcohol Detections (Total)', field: 'exceed' },
    { header: 'Email Address', field: 'email' },
    { header: 'Registered Date', field: 'create_time', customExport: csvDateTime },
  ];

  // EV-493。「利用可能」の機器だけをサーバーで絞って検索し、1ページずつ読む（以前は全件を読んで画面で絞っていた）。
  const deviceSearch = { fetchPage: assignableDevicePage(), resetKey: candidateVersion };

  const title = `${t('add-driver.title')}`;
  const save = `${t('add-driver.add')}`;
  const fields: Field[] = [
    // I12-2（2026-11-03 UAT）。携帯番号は運転者の ID なので必須。サーバーが国番号つき・ハイフンなしにそろえ、
    // 会社の中の重複を断る（項目のエラーで返る）。
    {
      type: 'text',
      label: `${t('add-driver.contact')}`,
      required: true,
      placeholder: t('add-driver.contact-placeholder'),
    },
    { type: 'text', label: `${t('add-driver.driver-name')}`, required: true },
    { type: 'email', label: `${t('add-driver.email')}`, required: true },
    // I12-1。生年月日は年・月・日の3つの選択。
    { type: 'birthday', label: `${t('add-driver.dob')}`, required: true },
    {
      type: 'multi-select',
      label: `${t('add-driver.assigned-device')}`,
      search: deviceSearch,
      required: false,
      // EV-318。候補を読めなかったときの表示（既存の文言・失敗の種類・再読み込み）は、候補の欄が出す（EV-493）。
    },
  ];

  // 保存の完了（成否）を追加画面へ返す。
  const handleSave = (AddNewValues: { [key: string]: any }) =>
    AddNewDriverAPI(AddNewValues);

  return (
    <section className="flex flex-row justify-between w-full">
      <div className="flex w-2/5 justify-start space-x-2">
        <Search
          placeholder={t('table.search')}
          onSearchChange={handleSearchChange}
        />
      </div>
      <div className="flex flex-row space-x-2 items-center">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <AddNewPopUp
          label={title}
          title={title}
          fields={fields}
          save={save}
          onSave={handleSave}
        />
        <Export data={driverData} exportFields={exportFields} name="drivers" conditions={exportConditions} />
      </div>

      {/* Error Popup */}
      {errorPopupVisible && (
        <ErrorPopup
          title={popupTitle || t('ui.error', { ns: 'common' })}
          messages={errorMessages}
          onClose={() => setErrorPopupVisible(false)}
        />
      )}
    </section>
  );
};

export default Toolbar;
